import { FC, ReactNode, useState } from "react";
import { createPortal } from "react-dom";

export interface HoverCardProps {
  children: ReactNode;
  /** Rich content shown in a floating card while hovering the trigger. */
  content: ReactNode;
  /** Whether the trigger stretches to fill its row. */
  fill?: boolean;
  width?: number;
}

const ESTIMATED_HEIGHT = 340;
const GAP = 8;
const MARGIN = 8;

/** Floating card on hover (web only). */
const HoverCard: FC<HoverCardProps> = ({
  children,
  content,
  fill = false,
  width = 320,
}) => {
  const [anchor, setAnchor] = useState<DOMRect | null>(null);

  const show = (event: React.MouseEvent<HTMLSpanElement>) =>
    setAnchor(event.currentTarget.getBoundingClientRect());

  const left = anchor
    ? Math.max(
        MARGIN,
        Math.min(
          anchor.left + anchor.width / 2 - width / 2,
          window.innerWidth - width - MARGIN
        )
      )
    : 0;
  const below = anchor
    ? anchor.bottom + GAP + ESTIMATED_HEIGHT < window.innerHeight
    : true;

  return (
    <>
      <span
        onMouseEnter={show}
        onMouseLeave={() => setAnchor(null)}
        style={{
          display: fill ? "flex" : "inline-flex",
          flex: fill ? 1 : undefined,
        }}
      >
        {children}
      </span>
      {anchor &&
        createPortal(
          <div
            style={{
              background: "rgb(var(--color-background-0))",
              border: "1px solid rgb(var(--color-outline-200))",
              borderRadius: 12,
              boxShadow: "0 12px 32px rgba(15, 23, 42, 0.14)",
              left,
              padding: 16,
              pointerEvents: "none",
              position: "fixed",
              width,
              zIndex: 10000,
              ...(below
                ? { top: anchor.bottom + GAP }
                : { bottom: window.innerHeight - anchor.top + GAP }),
            }}
          >
            {content}
          </div>,
          document.body
        )}
    </>
  );
};

export default HoverCard;
